import moment from 'moment'
import icStarLight from 'assets/images/ic-star-light.svg'
import icStarDark from 'assets/images/ic-star-dark.svg'
import axios from 'axios'
import { Alert } from 'avo-ui'
import DOMPurify from 'dompurify'

const regexEmail =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const regexSlug = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/

const setCurrency = (number = 0) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(
        number,
    )
}

const setNumberWithPoint = (number) => {
    return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

const separateNumber = (x, sp = '.') => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, sp)

const capitalize = (str) => {
    if (typeof str !== 'string' || str.length === 0) return str
    const words = str.split(' ')
    const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    return capitalizedWords.join(' ')
}

const formatDateMoment = (date, format) => {
    if (!date) return '-'
    return moment(date).format(format)
}

const formatUnixDateMoment = (date, format) => {
    return moment.unix(date).format(format)
}

const getAllQueryParams = (searchParams) => {
    const params = {}
    searchParams.forEach((value, key) => {
        params[key] = value
    })
    return params
}

const getQueryFilter = (filter) => {
    const queryFilter = { ...filter }
    Object.entries(queryFilter).forEach(([key, item]) => {
        queryFilter[key] = item.join(',')
    })
    return queryFilter
}

const setQueryParams = (queryParams) => {
    const params = { ...queryParams }
    for (const object in params) {
        if (!params[object]) delete params[object]
    }
    return params
}

const getRatingImage = (count) => {
    const stars = []
    for (let i = 1; i <= 5; i++) {
        if (i <= count) {
            stars.push(icStarLight)
        } else {
            stars.push(icStarDark)
        }
    }
    return stars
}

const convertStatusText = (text) => {
    switch (text) {
        case 'on-hold':
            return 'on hold'
        case 'pending-payment':
            return 'pending payment'
        case 'on-delivery':
            return 'delivery'
        default:
            return text
    }
}

const getHeight = (length) => {
    switch (true) {
        case length > 10:
            return 'h-[300mm]'
        case length > 5:
            return 'h-[150mm]'
        default:
            return 'h-[140mm]'
    }
}

const isLogin = () => {
    return !!localStorage.getItem('is_login')
}

const downloadFile = (url, blob, name, target) => {
    const source = url || window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = source
    link.target = target
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const constructOptionFilter = (rawOptions = [], nameKey, valueKey) => {
    return rawOptions?.map((i) => {
        return { name: i[nameKey || 'name'], value: i[valueKey || 'value'] }
    })
}

const constructOptionsSelect = (rawOptions = [], labelKey, valueKey, thirdValue) => {
    return rawOptions?.map((option) => {
        let object = {
            label: labelKey ? option[labelKey] : option,
            value: !!valueKey ? option[valueKey] : option,
        }
        if (!!thirdValue) {
            object[thirdValue] = option[thirdValue]
        }
        return object
    })
}

const constructOptionMultipleSelect = (rawOptions = [], labelKey, valueKey, imageKey, fourthKey) => {
    return rawOptions?.map((i) => {
        const object = { label: i[labelKey], value: i[valueKey], image_url: i[imageKey || 'image_url'] || null }
        if (fourthKey) object[fourthKey] = i[fourthKey]
        return object
    })
}

const constructSelectedValue = (item, labelKey, valueKey) => {
    return item ? { label: item[labelKey], value: item[valueKey] } : null
}

const uploadFileToS3 = async (item, file) => {
    try {
        await axios.post(
            `${item.url}`,
            {
                ...item.fields,
                file: file?.url || file,
            },
            {
                headers: { 'Content-Type': 'multipart/form-data' },
            },
        )
    } catch (err) {
        return Alert({
            icon: 'error',
            title: 'Error',
            message: 'Upload File Failed',
        })
    }
}

const generatePasswordAlphanumeric = () => {
    let result = ''
    let indexChar = 0
    const characters = [
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ', //uppercase
        'abcdefghijklmnopqrstuvwxyz', //lowercase
        '0123456789', //number
        '!@#$%^&*(),.?":{}|<>_+=-[]/~`', //special character
    ]
    for (let i = 0; i < 8; i++) {
        const randomIndex = Math.floor(Math.random() * characters[indexChar].length)
        result += characters[indexChar].charAt(randomIndex)
        if (indexChar < characters.length - 1) {
            indexChar += 1
        } else {
            indexChar = 0
        }
    }
    return result
}

const deepClone = (object) => {
    return JSON.parse(JSON.stringify(object))
}

const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0
}

const getErrorValue = (currentValue, columnName, replaceValue) => {
    return columnName
        ? currentValue?.message?.replace(replaceValue || currentValue?.parameter, columnName)
        : currentValue?.message
}

const getParamName = (param) => {
    return param?.replace(/\[.*]/gi, '.*')
}

const getIndexParam = (param) => {
    return Number(param?.replace(/[^0-9]/g, ''))
}

const convertErrorMessage = (dataError = [], columnName = {}, itemsColumnName = {}) => {
    return dataError?.reduce((accumulator, currentValue) => {
        const splitParam = currentValue?.parameter?.split('.')
        const newValue = {}
        if (!splitParam[1]) {
            newValue[splitParam[0]] = getErrorValue(currentValue, columnName[splitParam[0]])
        } else if (splitParam[0]?.includes('[')) {
            const getParam = splitParam[0]?.split('[')
            const paramName = getParamName(currentValue?.parameter)
            newValue[`${getParam[0]}_detail`] = [
                ...(accumulator[`${getParam[0]}_detail`] || []),
                {
                    index: getIndexParam(getParam[1]),
                    key: splitParam[1],
                    message: getErrorValue(currentValue, itemsColumnName[splitParam[1]], paramName),
                },
            ]
        } else if (!splitParam[1]?.includes('[')) {
            newValue[splitParam[0]] = {
                ...(accumulator[splitParam[0]] || {}),
                [splitParam[1]]: getErrorValue(currentValue, columnName[splitParam[0]]?.[splitParam[1]]),
            }
        } else {
            const getParam = splitParam[1]?.split('[')
            const paramName = getParamName(currentValue?.parameter)
            newValue[splitParam[0]] = {
                ...(accumulator[splitParam[0]] || {}),
                [`${getParam[0]}_detail`]: [
                    ...(accumulator[splitParam[0]]?.[`${getParam[0]}_detail`] || []),
                    {
                        index: getIndexParam(getParam[1]),
                        key: splitParam[2],
                        message: getErrorValue(currentValue, itemsColumnName[splitParam[2]], paramName),
                    },
                ],
            }
        }

        return { ...accumulator, ...newValue }
    }, {})
}

const getValueFromArray = (arr = [], key) => {
    return arr?.map((i) => i[key])
}

const getObjectName = (object = {}, startRange = 0, endRange = 0) => {
    const getData = Object?.entries(object)
    const sliceData = endRange ? getData.slice(startRange, endRange) : getData.slice(startRange)
    return sliceData?.map((entry) => entry[0])
}

const getSectionError = (errorForm, sectionFields) => {
    const dataSections = []
    Object?.keys(sectionFields).map((keySection) => {
        Object?.keys(errorForm).map((keyError) => {
            if (!dataSections?.includes(keySection) && sectionFields[keySection]?.includes(keyError)) {
                dataSections?.push(keySection)
            }
        })
    })

    return dataSections
}

const getSelectedOption = (options, value) => {
    const findData = options?.find((item) => value === item?.value || value === item?.label)
    return findData
}

const innerHTML = (html) => {
    return { __html: DOMPurify.sanitize(html) }
}

const replaceAll = (str, search, replacement) => {
    if (typeof str !== 'string') {
        throw new Error('First argument must be a string')
    }
    return str.split(search).join(replacement)
}

const decodeHTML = (html) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    return doc.documentElement.textContent
}

export {
    regexEmail,
    regexSlug,
    setCurrency,
    setNumberWithPoint,
    separateNumber,
    capitalize,
    formatDateMoment,
    formatUnixDateMoment,
    getAllQueryParams,
    setQueryParams,
    getQueryFilter,
    getRatingImage,
    convertStatusText,
    getHeight,
    isLogin,
    downloadFile,
    constructOptionFilter,
    constructOptionsSelect,
    constructOptionMultipleSelect,
    constructSelectedValue,
    uploadFileToS3,
    generatePasswordAlphanumeric,
    deepClone,
    isEmptyObject,
    convertErrorMessage,
    getValueFromArray,
    getObjectName,
    getSectionError,
    getSelectedOption,
    innerHTML,
    replaceAll,
    decodeHTML,
}
